import React, { useMemo } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useCaeCoverageMatrix } from '../../../hooks/analytics/useCaeReports.ts';
import { WidgetCard } from '../controller/shared.tsx';

/**
 * Report #12 — Risk-to-Control Coverage Matrix.
 *
 * Display-choice note: the docx sketches a risk × control-type matrix, but
 * the backend row (per docx's own SQL, verified against
 * analytics_reports_repository.go) is one row per risk with a single
 * `mapped_controls` count — no control-type breakdown. Rendered as a heat
 * table (rows = risks, coverage bar per row) with unmapped risks
 * (mapped_controls = 0) highlighted red, which is the closest faithful
 * rendering of the actual data shape.
 */
export const CoverageMatrixTable: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCaeCoverageMatrix();
  const rows = data ?? [];

  const maxMapped = useMemo(() => Math.max(1, ...rows.map((r) => r.mapped_controls)), [data]);
  const unmappedCount = useMemo(() => rows.filter((r) => r.mapped_controls === 0).length, [data]);

  return (
    <WidgetCard
      title="Risk-to-Control Coverage Matrix"
      description="Controls mapped per risk; unmapped risks are highlighted"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load coverage matrix.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No risks recorded."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
      action={
        unmappedCount > 0 ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
            <AlertTriangle className="h-3 w-3" />
            {unmappedCount} unmapped
          </span>
        ) : null
      }
    >
      <div className="analytics-scroll max-h-96 overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500 sticky top-0 bg-white">
              <th className="pb-2">Risk</th>
              <th className="pb-2">Level</th>
              <th className="pb-2 w-1/3">Coverage</th>
              <th className="pb-2 text-right">Controls</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.map((row, idx) => {
              const unmapped = row.mapped_controls === 0;
              return (
                <tr key={row.risk_id ?? idx} className={unmapped ? 'bg-red-50' : undefined}>
                  <td className="py-2 pr-2 text-gray-900 max-w-xs truncate" title={row.risk_description ?? undefined}>
                    {row.risk_id ?? '—'} — {row.risk_description ?? 'Untitled risk'}
                  </td>
                  <td className="py-2 pr-2 text-gray-600 capitalize">{row.risk_level ?? '—'}</td>
                  <td className="py-2 pr-2">
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${unmapped ? 'bg-red-400' : 'bg-green-500'}`}
                        style={{ width: `${Math.max(4, (100 * row.mapped_controls) / maxMapped)}%` }}
                      />
                    </div>
                  </td>
                  <td className={`py-2 text-right font-medium ${unmapped ? 'text-red-600' : 'text-gray-700'}`}>
                    {row.mapped_controls}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </WidgetCard>
  );
};

export default CoverageMatrixTable;
